import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  Wallet,
  CalendarDays,
  LogOut,
  Clock,
  Receipt,
  Calculator,
  Ticket,
  HelpCircle,
  Database,
  Building2,
  TrendingUp,
  Shield,
  Cog,
  FileText,
} from 'lucide-react';

const Sidebar = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const adminLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/employee-master', icon: Database, label: 'Employee Master', badge: 'NEW' },
    { to: '/attendance', icon: Clock, label: 'Attendance' },
    { to: '/salary', icon: Wallet, label: 'Salary Management' },
    { to: '/payroll-engine', icon: Cog, label: 'Payroll Engine', badge: 'ADV' },
    { to: '/insurance', icon: Shield, label: 'Insurance', badge: 'NEW' },
    { to: '/leave-management', icon: CalendarDays, label: 'Leave Management' },
    { to: '/claims', icon: Receipt, label: 'Claims' },
    { to: '/promotion-transfer', icon: TrendingUp, label: 'Promotion & Transfer' },
    { to: '/tax-declarations', icon: Calculator, label: 'Tax & Declarations' },
    { to: '/help-desk', icon: Ticket, label: 'Help Desk' },
    { to: '/sop-documents', icon: FileText, label: 'SOP Documents', badge: 'NEW' },
  ];

  const verifierLinks = [
    { to: '/verifier-dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/employee-master', icon: Database, label: 'Employee Master', badge: 'NEW' },
    { to: '/attendance', icon: Clock, label: 'Attendance' },
    { to: '/salary', icon: Wallet, label: 'Salary Management' },
    { to: '/payroll-engine', icon: Cog, label: 'Payroll Engine', badge: 'ADV' },
    { to: '/insurance', icon: Shield, label: 'Insurance', badge: 'NEW' },
    { to: '/leave-management', icon: CalendarDays, label: 'Leave Management' },
    { to: '/claims', icon: Receipt, label: 'Claims' },
    { to: '/promotion-transfer', icon: TrendingUp, label: 'Promotion & Transfer' },
    { to: '/tax-declarations', icon: Calculator, label: 'Tax & Declarations' },
    { to: '/help-desk', icon: Ticket, label: 'Help Desk' },
  ];

  const employeeLinks = [
    { to: '/employee-dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/attendance', icon: Clock, label: 'Attendance' },
    { to: '/my-payslips', icon: Wallet, label: 'My Payslips' },
    { to: '/my-insurance', icon: Shield, label: 'Insurance', badge: 'NEW' },
    { to: '/my-leaves', icon: CalendarDays, label: 'My Leaves' },
    { to: '/claims', icon: Receipt, label: 'My Claims' },
    { to: '/promotion-transfer', icon: TrendingUp, label: 'Promotion & Transfer' },
    { to: '/tax-declarations', icon: Calculator, label: 'Tax Declarations' },
    { to: '/help-desk', icon: HelpCircle, label: 'Help Desk' },
  ];

  const links = isAdmin() ? adminLinks : (user?.role === 'verifier' ? verifierLinks : employeeLinks);

  return (
    <aside className="fixed left-0 top-0 h-screen w-64 bg-slate-900 text-white flex flex-col z-50" data-testid="sidebar">
      {/* Logo/Brand */}
      <div className="p-6 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-terra-500 rounded-lg flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-semibold text-lg tracking-tight">HRMS Solution</h1>
            <p className="text-xs text-slate-400">by Niyanta.io</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {links.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                isActive
                  ? 'bg-white text-slate-900 font-medium shadow-md'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`
            }
          >
            <link.icon className="w-5 h-5" strokeWidth={1.5} />
            <span className="flex-1">{link.label}</span>
            {link.badge && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-emerald-500 text-white rounded">
                {link.badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3 px-4 py-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
            <span className="text-sm font-medium">{user?.name?.charAt(0).toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-red-500/10 hover:text-red-400 transition-colors duration-200"
        >
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
